import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Search,
  MessageCircle,
  Sparkles,
  Check,
  EyeOff,
  Star,
  Calendar,
  TrendingUp,
  RefreshCw,
  Copy,
} from "lucide-react";
import { format, differenceInCalendarDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { calculateClientScore } from "@/lib/clientScore";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useUserCurrency";

type Loan = {
  id: string;
  clientId: string;
  clientName: string;
  phone: string | null;
  amount: number;
  interestRate: number;
  interestType: "monthly" | "fixed" | "daily";
  status: "paid" | "active" | "overdue";
  dueDate: Date;
  loanDate: Date;
  updatedAt: Date;
};

type Payment = {
  loanId: string;
  paidAt: Date;
  interestAmount: number;
  principalAmount: number;
  amount: number;
};

type Client = {
  id: string;
  name: string;
  phone: string | null;
  createdAt: Date;
};

interface Props {
  loans: Loan[];
  payments: Payment[];
  clients: Client[];
}

const STORAGE_KEY = "intel_remarketing_state_v1";

type State = Record<string, "ignored" | "contacted" | undefined>;

function loadState(): State {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveState(s: State) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function RemarketingTab({ loans, payments, clients }: Props) {
  const { format: fmtMoney } = useCurrency();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<
    "all" | "active" | "ignored" | "contacted" | "good" | "score80"
  >("all");
  const [sort, setSort] = useState<"chance" | "recent">("chance");
  const [state, setState] = useState<State>(loadState());
  const [msgClient, setMsgClient] = useState<{
    clientId: string;
    name: string;
    phone: string | null;
    text: string;
    lastAmount: number;
    daysSince: number;
    variant: number;
    hasSettledHistory: boolean;
  } | null>(null);

  const scoreLoans = loans.map((l) => ({
    id: l.id,
    clientId: l.clientId,
    amount: l.amount,
    dueDate: l.dueDate,
    loanDate: l.loanDate,
    status: l.status,
    interestType: l.interestType,
  }));
  const scorePayments = payments.map((p) => ({
    loanId: p.loanId,
    paidAt: p.paidAt,
    interestAmount: p.interestAmount,
  }));

  const candidates = useMemo(() => {
    // Cliente elegível: NÃO possui nenhum loan ativo/overdue agora.
    // Inclui:
    //  - clientes que já tiveram empréstimo e quitaram
    //  - clientes cadastrados sem nenhum empréstimo ativo (potenciais novos contratos)
    const byClient = new Map<string, Loan[]>();
    for (const l of loans) {
      if (!byClient.has(l.clientId)) byClient.set(l.clientId, []);
      byClient.get(l.clientId)!.push(l);
    }
    const paymentsByLoan = new Map<string, Payment[]>();
    for (const p of payments) {
      if (!paymentsByLoan.has(p.loanId)) paymentsByLoan.set(p.loanId, []);
      paymentsByLoan.get(p.loanId)!.push(p);
    }

    const result: Array<{
      clientId: string;
      clientName: string;
      phone: string | null;
      lastAmount: number;
      lastSettledAt: Date;
      hasSettledHistory: boolean;
      totalLoans: number;
      settledLoans: number;
      score: ReturnType<typeof calculateClientScore>;
      returnChance: number;
    }> = [];

    for (const client of clients) {
      const list = byClient.get(client.id) || [];
      const hasOpen = list.some(
        (l) => l.status === "active" || l.status === "overdue"
      );
      if (hasOpen) continue; // ainda tem contrato em aberto -> não é remarketing

      // Determina referência de "última quitação" / contato
      let lastAmount = 0;
      let lastSettledAt = client.createdAt;
      let mostRecentPayment: Date | null = null;
      const paidLoans = list.filter((l) => l.status === "paid");
      const hasSettledHistory = paidLoans.length > 0;

      // Só entra no Remarketing quem tem ao menos 1 empréstimo quitado.
      // Evita cards com dados zerados de clientes sem histórico.
      if (!hasSettledHistory) continue;

      if (list.length > 0) {
        const sorted = [...list].sort(
          (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
        );
        const lastLoan = sorted[0];
        lastAmount = lastLoan.amount;
        lastSettledAt = lastLoan.updatedAt;
        for (const l of list) {
          const ps = paymentsByLoan.get(l.id) || [];
          for (const p of ps) {
            if (!mostRecentPayment || p.paidAt > mostRecentPayment) {
              mostRecentPayment = p.paidAt;
            }
          }
        }
        if (mostRecentPayment) lastSettledAt = mostRecentPayment;
      }

      const score = calculateClientScore(client.id, scoreLoans, scorePayments);
      const days = differenceInCalendarDays(new Date(), lastSettledAt);
      let chance = score.score;
      if (days <= 30) chance += 5;
      else if (days > 180) chance -= 15;
      chance = Math.max(0, Math.min(100, chance));

      result.push({
        clientId: client.id,
        clientName: client.name,
        phone: client.phone,
        lastAmount,
        lastSettledAt,
        hasSettledHistory,
        totalLoans: list.length,
        settledLoans: paidLoans.length,
        score,
        returnChance: chance,
      });
    }
    return result.sort((a, b) => b.returnChance - a.returnChance);
  }, [loans, payments, clients]);

  const filtered = candidates
    .filter((c) => {
      const s = state[c.clientId];
      if (filter === "active" && s) return false;
      if (filter === "ignored" && s !== "ignored") return false;
      if (filter === "contacted" && s !== "contacted") return false;
      if (filter === "good" && c.score.label !== "Bom pagador") return false;
      if (filter === "score80" && c.score.score < 80) return false;
      if (search && !c.clientName.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    })
    .sort((a, b) => {
      if (sort === "recent")
        return b.lastSettledAt.getTime() - a.lastSettledAt.getTime();
      return b.returnChance - a.returnChance;
    });

  const setStatus = (clientId: string, value: "ignored" | "contacted" | undefined) => {
    const next = { ...state };
    if (value === undefined) delete next[clientId];
    else next[clientId] = value;
    setState(next);
    saveState(next);
  };

  const messageTemplates = (
    name: string,
    lastAmount: number,
    daysSince: number,
    hasSettledHistory: boolean
  ): string[] => {
    const first = name.split(" ")[0];
    const valor = fmtMoney(lastAmount);

    // Cliente sem histórico de quitação (cadastrado mas nunca pegou/quitou empréstimo)
    // -> mensagens de prospecção, SEM mencionar "quitou" ou "faz X dias"
    if (!hasSettledHistory) {
      return [
        `Olá ${first}! Tudo bem? 👋\n\nEstou com condições especiais de crédito disponíveis e lembrei de você. Posso te enviar uma proposta personalizada agora mesmo, com aprovação rápida.\n\nQuer saber as condições?`,
        `Oi ${first}, tudo certo? 😊\n\nTenho uma proposta de crédito que pode te ajudar. Aprovação no mesmo dia e sem burocracia.\n\nPosso te passar os detalhes?`,
        `${first}, e aí! 💼\n\nSe estiver precisando de um reforço no caixa, posso liberar uma proposta agora mesmo. Análise rápida e condições justas.\n\nMe avisa se tiver interesse!`,
        `Olá ${first}! 🌟\n\nEstou liberando crédito com condições diferenciadas esta semana. Quer simular um valor sem compromisso?`,
        `Oi ${first}, beleza? 👋\n\nLembrei de você hoje. Caso precise de um valor pra organizar as contas ou investir, posso preparar uma proposta personalizada agora.\n\nTopa?`,
      ];
    }

    // Cliente com histórico de quitação -> mensagens de relacionamento
    const tempoTexto =
      daysSince <= 0
        ? "recentemente"
        : daysSince === 1
        ? "ontem"
        : daysSince < 30
        ? `há ${daysSince} dias`
        : daysSince < 60
        ? "há cerca de 1 mês"
        : `há ${Math.round(daysSince / 30)} meses`;

    const valorTrecho = lastAmount > 0 ? ` de ${valor}` : "";

    return [
      `Olá ${first}! Tudo bem? 👋\n\nVi que você quitou direitinho seu último contrato${valorTrecho} com a gente. Parabéns pelo compromisso! 🙌\n\nComo cliente bom pagador, posso liberar uma nova proposta personalizada hoje mesmo. Quer que eu te envie as condições?`,
      `Oi ${first}, tudo certo? 😊\n\nPassando para agradecer pela quitação do seu contrato. Foi um prazer te atender!\n\nSe precisar de um novo crédito, é só me chamar. Tenho condições especiais reservadas pra clientes como você.`,
      `${first}, e aí! 💼\n\nVocê quitou seu último empréstimo conosco ${tempoTexto}. Caso esteja precisando de um reforço no caixa, posso liberar uma nova proposta agora mesmo, com aprovação rápida.\n\nMe avisa se tiver interesse!`,
      `Olá ${first}! 🌟\n\nVocê está na nossa lista de clientes preferenciais por ter quitado tudo em dia. Isso te dá acesso a um novo crédito com condições diferenciadas.\n\nQuer simular um novo valor? É rápido!`,
      `Oi ${first}, beleza? 👋\n\nLembrei de você hoje. Seu histórico com a gente é excelente e isso abre portas pra novos valores e prazos melhores.\n\nPosso preparar uma proposta personalizada agora?`,
    ];
  };

  const buildMessage = (
    name: string,
    lastAmount: number,
    daysSince: number,
    hasSettledHistory: boolean,
    variant: number
  ) => {
    const templates = messageTemplates(name, lastAmount, daysSince, hasSettledHistory);
    return templates[variant % templates.length];
  };

  const openMessage = (c: {
    clientId: string;
    clientName: string;
    phone: string | null;
    lastAmount: number;
    lastSettledAt: Date;
    hasSettledHistory: boolean;
  }) => {
    const days = c.hasSettledHistory
      ? differenceInCalendarDays(new Date(), c.lastSettledAt)
      : 0;
    const variant = Math.floor(Math.random() * 5);
    setMsgClient({
      clientId: c.clientId,
      name: c.clientName,
      phone: c.phone,
      text: buildMessage(c.clientName, c.lastAmount, days, c.hasSettledHistory, variant),
      lastAmount: c.lastAmount,
      daysSince: days,
      variant,
      hasSettledHistory: c.hasSettledHistory,
    });
  };

  const regenerateMessage = () => {
    if (!msgClient) return;
    const next = (msgClient.variant + 1) % 5;
    setMsgClient({
      ...msgClient,
      variant: next,
      text: buildMessage(
        msgClient.name,
        msgClient.lastAmount,
        msgClient.daysSince,
        msgClient.hasSettledHistory,
        next
      ),
    });
  };

  const sendWhatsApp = (phone: string | null, text: string, clientId: string) => {
    if (!phone) {
      toast.error("Cliente sem telefone cadastrado");
      return;
    }
    const digits = phone.replace(/\D/g, "");
    const url = `https://wa.me/55${digits}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    setStatus(clientId, "contacted");
  };

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="glass rounded-2xl p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{filtered.length} oportunidade(s)</span>
          <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
            <SelectTrigger className="w-[190px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="good">Bom pagador</SelectItem>
              <SelectItem value="score80">Score acima de 80</SelectItem>
              <SelectItem value="active">Não contatados</SelectItem>
              <SelectItem value="contacted">Contatados</SelectItem>
              <SelectItem value="ignored">Ignorados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v: any) => setSort(v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="chance">Maior chance de retorno</SelectItem>
              <SelectItem value="recent">Mais recente quitado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Lista */}
      {filtered.length === 0 ? (
        <div className="glass p-10 rounded-2xl text-center">
          <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Nenhum cliente elegível para remarketing no momento.
          </p>
          <p className="text-[11px] text-muted-foreground/70 mt-1">
            Clientes aparecem aqui quando quitam todos os contratos abertos.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((c) => {
            const s = state[c.clientId];
            const toneMap: Record<string, string> = {
              success: "text-success",
              warning: "text-gold",
              destructive: "text-destructive",
            };
            const dotMap: Record<string, string> = {
              success: "bg-success",
              warning: "bg-gold",
              destructive: "bg-destructive",
            };
            return (
              <Card key={c.clientId} className="relative overflow-hidden p-5 group">
                <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-success via-success/60 to-transparent`} />
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold tracking-tight truncate">{c.clientName}</h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Star className={`h-3 w-3 ${toneMap[c.score.tone]}`} />
                      <span className={`text-xs font-medium ${toneMap[c.score.tone]}`}>
                        {c.score.label} · {c.score.score}
                      </span>
                    </div>
                  </div>
                  {s === "contacted" && (
                    <Badge variant="default" className="shrink-0">Contatado</Badge>
                  )}
                  {s === "ignored" && (
                    <Badge variant="secondary" className="shrink-0">Ignorado</Badge>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Último valor</span>
                    <span className="tabular font-medium">
                      {fmtMoney(c.lastAmount)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Empréstimos feitos</span>
                    <span className="tabular font-medium">{c.totalLoans}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Quitação
                    </span>
                    <span className="tabular">
                      {format(c.lastSettledAt, "dd/MM/yyyy", { locale: ptBR })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Tempo desde último contrato</span>
                    <span className="tabular">
                      {differenceInCalendarDays(new Date(), c.lastSettledAt)} dias
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" /> Chance de retorno
                    </span>
                    <span className="tabular font-semibold">
                      {c.returnChance}% ·{" "}
                      {c.returnChance >= 70 ? "Alta" : c.returnChance >= 40 ? "Média" : "Baixa"}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full ${dotMap[c.score.tone]}`}
                      style={{ width: `${c.returnChance}%` }}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openMessage(c)}
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Mensagem
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => {
                      const days = c.hasSettledHistory
                        ? differenceInCalendarDays(new Date(), c.lastSettledAt)
                        : 0;
                      const text = buildMessage(
                        c.clientName,
                        c.lastAmount,
                        days,
                        c.hasSettledHistory,
                        0
                      );
                      sendWhatsApp(c.phone, text, c.clientId);
                    }}
                  >
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                  {s !== "contacted" && (
                    <Button size="sm" variant="ghost" onClick={() => setStatus(c.clientId, "contacted")}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Contatado
                    </Button>
                  )}
                  {s !== "ignored" ? (
                    <Button size="sm" variant="ghost" onClick={() => setStatus(c.clientId, "ignored")}>
                      <EyeOff className="h-3.5 w-3.5 mr-1" /> Ignorar
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setStatus(c.clientId, undefined)}>
                      Restaurar
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!msgClient} onOpenChange={(o) => !o && setMsgClient(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mensagem para {msgClient?.name}</DialogTitle>
          </DialogHeader>
          <textarea
            className="w-full min-h-[160px] rounded-md bg-card border border-border p-3 text-sm"
            value={msgClient?.text || ""}
            onChange={(e) => msgClient && setMsgClient({ ...msgClient, text: e.target.value })}
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={regenerateMessage}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refazer
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (msgClient) {
                  navigator.clipboard.writeText(msgClient.text);
                  toast.success("Mensagem copiada");
                }
              }}
            >
              <Copy className="h-4 w-4 mr-1" /> Copiar
            </Button>
            <Button
              onClick={() => {
                if (msgClient?.phone) {
                  const digits = msgClient.phone.replace(/\D/g, "");
                  window.open(
                    `https://wa.me/55${digits}?text=${encodeURIComponent(msgClient.text)}`,
                    "_blank"
                  );
                  setStatus(msgClient.clientId, "contacted");
                  setMsgClient(null);
                } else {
                  toast.error("Sem telefone cadastrado");
                }
              }}
            >
              <MessageCircle className="h-4 w-4 mr-1" /> Enviar WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}